import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useSpotlightSettings } from "@/hooks/useSpotlightSettings";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Star,
  Copy,
  ExternalLink,
  Loader2,
  Image,
  Eye,
  EyeOff,
  Home,
  BarChart3,
  Users,
  Phone,
  Palette,
  MapPin,
  TrendingUp,
  Bookmark,
  Calendar,
  MessageSquareQuote,
  Link2,
  Globe,
  Search,
  Sparkles,
} from "lucide-react";
import { SpotlightIntroModal } from "@/components/spotlight/SpotlightIntroModal";

export default function SpotlightManage() {
  const { toast } = useToast();
  const { settings, isLoading, save, isSaving } = useSpotlightSettings();
  const { getEffectiveUserId } = useImpersonation();

  // Local form state
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [ctaText, setCtaText] = useState("Get in Touch");
  const [ctaLink, setCtaLink] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [showHomesCarousel, setShowHomesCarousel] = useState(true);
  const [showTeamSection, setShowTeamSection] = useState(true);
  const [showViewsSaves, setShowViewsSaves] = useState(false);
  const [showMarketComparison, setShowMarketComparison] = useState(false);
  const [showSpeedToSold, setShowSpeedToSold] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [showShowings, setShowShowings] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [customPrimaryColor, setCustomPrimaryColor] = useState("");
  const [customSecondaryColor, setCustomSecondaryColor] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [slug, setSlug] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialLinkedin, setSocialLinkedin] = useState("");
  const [socialTwitter, setSocialTwitter] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialTiktok, setSocialTiktok] = useState("");
  const [zillowUsername, setZillowUsername] = useState("");
  const [customAvgDom, setCustomAvgDom] = useState("");
  const [heroMediaType, setHeroMediaType] = useState<string>("listing");
  const [heroCustomUrl, setHeroCustomUrl] = useState("");
  const [heroListingNorthId, setHeroListingNorthId] = useState<string>("");
  const [ctaListingNorthId, setCtaListingNorthId] = useState<string>("");
  const [isFetchingReviews, setIsFetchingReviews] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);

  // Show intro modal on first visit
  useEffect(() => {
    if (!localStorage.getItem("has_seen_spotlight_intro")) {
      setShowIntroModal(true);
    }
  }, []);

  const handleCloseIntro = () => {
    localStorage.setItem("has_seen_spotlight_intro", "1");
    setShowIntroModal(false);
  };

  // Get team ID for the spotlight link
  const { data: teamId } = useQuery({
    queryKey: ["spotlight-team-id"],
    queryFn: async () => {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return null;
      const { data } = await supabase
        .from("profiles")
        .select("team_id")
        .eq("user_id", effectiveUserId)
        .single();
      return data?.team_id ?? null;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Fetch team's published norths for the hero listing dropdown
  const { data: teamNorths } = useQuery({
    queryKey: ["spotlight-team-norths", teamId],
    queryFn: async () => {
      if (!teamId) return [];
      // Get all user_ids on this team
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("team_id", teamId);
      if (!profiles || profiles.length === 0) return [];
      const userIds = profiles.map((p) => p.user_id);
      // Fetch published norths with images
      const { data: norths } = await supabase
        .from("norths")
        .select("id, address, city, state, image_url, price")
        .in("user_id", userIds)
        .eq("is_published", true)
        .not("image_url", "is", null)
        .order("price", { ascending: false, nullsFirst: false });
      return norths ?? [];
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5,
  });

  // Sync form state when settings load
  useEffect(() => {
    if (settings) {
      setHeadline(settings.headline ?? "");
      setSubheadline(settings.subheadline ?? "");
      setCtaText(settings.cta_text ?? "Get in Touch");
      setCtaLink(settings.cta_link ?? "");
      setContactEmail(settings.contact_email ?? "");
      setContactPhone(settings.contact_phone ?? "");
      setShowHomesCarousel(settings.show_homes_carousel);
      setShowTeamSection(settings.show_team_section);
      setShowViewsSaves(settings.show_views_saves);
      setShowMarketComparison(settings.show_market_comparison);
      setShowSpeedToSold(settings.show_speed_to_sold ?? true);
      setShowMap(settings.show_map);
      setShowShowings(settings.show_showings);
      setShowReviews(settings.show_reviews);
      setCustomPrimaryColor(settings.custom_primary_color ?? "");
      setCustomSecondaryColor(settings.custom_secondary_color ?? "");
      setIsPublished(settings.is_published);
      setSlug(settings.slug ?? "");
      setSocialFacebook(settings.social_facebook ?? "");
      setSocialInstagram(settings.social_instagram ?? "");
      setSocialLinkedin(settings.social_linkedin ?? "");
      setSocialTwitter(settings.social_twitter ?? "");
      setSocialYoutube(settings.social_youtube ?? "");
      setSocialTiktok(settings.social_tiktok ?? "");
      setZillowUsername(settings.zillow_username ?? "");
      setCustomAvgDom(settings.custom_avg_dom != null ? String(settings.custom_avg_dom) : "");
      setHeroMediaType(settings.hero_media_type ?? "listing");
      setHeroCustomUrl(settings.hero_custom_url ?? "");
      setHeroListingNorthId(settings.hero_listing_north_id ?? "");
      setCtaListingNorthId(settings.cta_listing_north_id ?? "");
    }
  }, [settings]);

  const spotlightUrl = teamId
    ? `${window.location.origin}/spotlight/${teamId}`
    : null;
  const vanityUrl = slug
    ? `${window.location.origin}/s/${slug}`
    : null;

  const handleSave = async () => {
    try {
      await save({
        headline: headline || null,
        subheadline: subheadline || null,
        cta_text: ctaText || "Get in Touch",
        cta_link: ctaLink || null,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
        show_homes_carousel: showHomesCarousel,
        show_team_section: showTeamSection,
        show_views_saves: showViewsSaves,
        show_market_comparison: showMarketComparison,
        show_speed_to_sold: showSpeedToSold,
        show_map: showMap,
        show_showings: showShowings,
        show_reviews: showReviews,
        custom_primary_color: customPrimaryColor || null,
        custom_secondary_color: customSecondaryColor || null,
        is_published: isPublished,
        slug: slug || null,
        social_facebook: socialFacebook || null,
        social_instagram: socialInstagram || null,
        social_linkedin: socialLinkedin || null,
        social_twitter: socialTwitter || null,
        social_youtube: socialYoutube || null,
        social_tiktok: socialTiktok || null,
        zillow_username: zillowUsername || null,
        custom_avg_dom: customAvgDom ? parseInt(customAvgDom, 10) : null,
        hero_media_type: heroMediaType,
        hero_custom_url: heroCustomUrl || null,
        hero_listing_north_id: heroListingNorthId || null,
        cta_listing_north_id: ctaListingNorthId || null,
      });
      toast({ title: "Spotlight settings saved" });
    } catch {
      // Error toast handled by the hook
    }
  };

  const handleTogglePublish = async () => {
    const newState = !isPublished;
    setIsPublished(newState);
    try {
      await save({ is_published: newState });
      toast({
        title: newState ? "Spotlight published!" : "Spotlight unpublished",
        description: newState
          ? "Your Spotlight page is now live."
          : "Your Spotlight page is no longer visible.",
      });
    } catch {
      setIsPublished(!newState); // Revert on error
    }
  };

  const handleCopyLink = () => {
    if (spotlightUrl) {
      navigator.clipboard.writeText(spotlightUrl);
      toast({ title: "Link copied to clipboard" });
    }
  };

  const handleFetchReviews = async () => {
    if (!zillowUsername.trim() || !teamId) return;
    setIsFetchingReviews(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || "https://zkqcqgjbsigjdwzmnwmp.supabase.co"}/functions/v1/fetch-zillow-reviews`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ teamId, zillowUsername: zillowUsername.trim() }),
        }
      );
      const result = await res.json();
      console.log("[fetch-zillow] Full response:", JSON.stringify(result, null, 2));
      if (!res.ok) {
        toast({ title: "Error fetching Zillow data", description: result.error, variant: "destructive" });
      } else {
        toast({
          title: "Fetched Zillow Info",
          description: `Fetched last ${result.reviewCount ?? 0} reviews. Fetched last ${result.listingsCount ?? 0} sold homes.`,
        });
      }
    } catch (err: any) {
      toast({ title: "Error fetching Zillow data", description: err.message, variant: "destructive" });
    } finally {
      setIsFetchingReviews(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const pageViews = settings?.page_views_count ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-8 max-w-3xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Spotlight</h1>
            <div className="flex items-center gap-2 ml-auto">
              <Badge variant={isPublished ? "default" : "secondary"}>
                {isPublished ? "Published" : "Draft"}
              </Badge>
              {isPublished && pageViews > 0 && (
                <Badge variant="outline" className="gap-1">
                  <Eye className="h-3 w-3" />
                  {pageViews.toLocaleString()} view{pageViews !== 1 ? "s" : ""}
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1 ml-9">
            A shareable page showcasing your team's track record
          </p>
          <button
            onClick={() => setShowIntroModal(true)}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1 ml-9"
          >
            <Sparkles className="h-3 w-3" />
            What is Spotlight?
          </button>
        </div>

        {/* Publish / Link Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {isPublished ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              Visibility
            </CardTitle>
            <CardDescription>
              Control whether your Spotlight page is publicly accessible
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">
                  {isPublished ? "Your Spotlight is live" : "Your Spotlight is hidden"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isPublished
                    ? "Anyone with the link can view it"
                    : "Only you can see the preview"}
                </p>
              </div>
              <Switch
                checked={isPublished}
                onCheckedChange={handleTogglePublish}
                disabled={isSaving}
              />
            </div>
            {(spotlightUrl || vanityUrl) && (
              <div className="space-y-2">
                {vanityUrl && (
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={vanityUrl}
                      className="text-xs font-mono"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(vanityUrl);
                        toast({ title: "Vanity link copied to clipboard" });
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={vanityUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                )}
                {spotlightUrl && (
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={spotlightUrl}
                      className="text-xs font-mono"
                    />
                    <Button variant="outline" size="sm" onClick={handleCopyLink}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={spotlightUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Content</CardTitle>
            <CardDescription>Customize your headline and messaging</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="headline">Headline</Label>
              <Input
                id="headline"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Leave blank to auto-generate from your data"
              />
              <p className="text-xs text-muted-foreground">
                If left blank, a headline is automatically generated from your career sales data. Not required.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subheadline">Subheadline</Label>
              <Input
                id="subheadline"
                value={subheadline}
                onChange={(e) => setSubheadline(e.target.value)}
                placeholder="Leave blank to auto-generate from your data"
              />
              <p className="text-xs text-muted-foreground">
                If left blank, a tagline is automatically generated from your career stats, speed-to-sold advantage, and recent transactions.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Hero Background */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Image className="h-4 w-4" />
              Hero Background
            </CardTitle>
            <CardDescription>
              Choose what appears behind your headline in the full-screen hero section
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup value={heroMediaType} onValueChange={setHeroMediaType} className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <RadioGroupItem value="listing" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Featured Listing Photo</p>
                  <p className="text-xs text-muted-foreground">Uses the first photo from your published listings</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <RadioGroupItem value="photo" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Custom Photo</p>
                  <p className="text-xs text-muted-foreground">Link to your own hero image — team photo, drone shot, etc.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <RadioGroupItem value="video" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Custom Video</p>
                  <p className="text-xs text-muted-foreground">Link to an MP4 video that auto-plays silently in the background</p>
                </div>
              </label>
            </RadioGroup>

            {heroMediaType === "listing" && teamNorths && teamNorths.length > 0 && (
              <div className="space-y-2 pt-2">
                <Label>Featured Listing</Label>
                <Select value={heroListingNorthId} onValueChange={setHeroListingNorthId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Most expensive listing (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Most expensive listing (default)</SelectItem>
                    {teamNorths.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.address}, {n.city}, {n.state}
                        {n.price ? ` — $${n.price.toLocaleString()}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose which listing photo appears as the hero background
                </p>
              </div>
            )}

            {(heroMediaType === "photo" || heroMediaType === "video") && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="heroCustomUrl">
                  {heroMediaType === "photo" ? "Image URL" : "Video URL (MP4)"}
                </Label>
                <Input
                  id="heroCustomUrl"
                  value={heroCustomUrl}
                  onChange={(e) => setHeroCustomUrl(e.target.value)}
                  placeholder={heroMediaType === "photo" ? "https://example.com/hero.jpg" : "https://example.com/hero.mp4"}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sections Toggle */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Sections</CardTitle>
            <CardDescription>Choose which sections appear on your page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Proven Track Record</p>
                  <p className="text-xs text-muted-foreground">Full-screen animated showcase of career sales, average sale price, and price range from your Zillow profile</p>
                </div>
              </div>
              <Switch checked={showSpeedToSold} onCheckedChange={setShowSpeedToSold} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bookmark className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Digital Footprint</p>
                  <p className="text-xs text-muted-foreground">Per-platform breakdown of social media engagement and portal listing views from Zillow and Realtor.com</p>
                </div>
              </div>
              <Switch checked={showViewsSaves} onCheckedChange={setShowViewsSaves} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Market Comparison</p>
                  <p className="text-xs text-muted-foreground">Side-by-side days on market comparison against the local market average with demand rating</p>
                </div>
              </div>
              <Switch checked={showMarketComparison} onCheckedChange={setShowMarketComparison} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Listings Carousel</p>
                  <p className="text-xs text-muted-foreground">Scrollable photo cards of your published listings with price, details, and view counts</p>
                </div>
              </div>
              <Switch checked={showHomesCarousel} onCheckedChange={setShowHomesCarousel} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Coverage Map</p>
                  <p className="text-xs text-muted-foreground">Interactive map pinning your sold homes from Zillow to show your geographic reach</p>
                </div>
              </div>
              <Switch checked={showMap} onCheckedChange={setShowMap} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Showing Activity</p>
                  <p className="text-xs text-muted-foreground">Calendar heat map of recent showings and open houses across your listings</p>
                </div>
              </div>
              <Switch checked={showShowings} onCheckedChange={setShowShowings} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquareQuote className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Client Reviews</p>
                  <p className="text-xs text-muted-foreground">5-star reviews imported from your Zillow agent profile</p>
                </div>
              </div>
              <Switch checked={showReviews} onCheckedChange={setShowReviews} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Meet the Team</p>
                  <p className="text-xs text-muted-foreground">Headshots and names of your team members</p>
                </div>
              </div>
              <Switch checked={showTeamSection} onCheckedChange={setShowTeamSection} />
            </div>
          </CardContent>
        </Card>

        {/* Zillow Integration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-4 w-4" />
              Zillow Integration
            </CardTitle>
            <CardDescription>
              Import reviews, sold listings, and sales data from your Zillow agent profile
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="zillowUsername">Zillow Username</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="zillowUsername"
                  value={zillowUsername}
                  onChange={(e) => setZillowUsername(e.target.value)}
                  placeholder="janedoe"
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFetchReviews}
                  disabled={isFetchingReviews || !zillowUsername.trim() || !teamId}
                >
                  {isFetchingReviews ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    "Fetch Zillow Data"
                  )}
                </Button>
              </div>
              {isFetchingReviews ? (
                <p className="text-xs text-amber-600">
                  This may take a few minutes. We're pulling reviews, sold listings, and checking price history for each property.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Your Zillow profile screen name from zillow.com/profile/&lt;username&gt;
                </p>
              )}
            </div>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="customAvgDom">Your Average Days on Market</Label>
              <Input
                id="customAvgDom"
                type="number"
                value={customAvgDom}
                onChange={(e) => setCustomAvgDom(e.target.value)}
                placeholder="e.g. 14"
                className="max-w-[150px]"
              />
              <p className="text-xs text-muted-foreground">
                Your team's average DOM — displayed alongside market averages in the Market Comparison section
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact / CTA */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Contact & CTA
            </CardTitle>
            <CardDescription>How potential clients can reach you</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ctaText">CTA Button Text</Label>
              <Input
                id="ctaText"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                placeholder="Get in Touch"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ctaLink">CTA Link</Label>
              <Input
                id="ctaLink"
                value={ctaLink}
                onChange={(e) => setCtaLink(e.target.value)}
                placeholder="https://yoursite.com/contact or mailto:team@example.com"
              />
              <p className="text-xs text-muted-foreground">
                URL, mailto:, or tel: link. Leave blank to use email/phone below.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="team@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            {teamNorths && teamNorths.length > 0 && (
              <div className="space-y-2 pt-2">
                <Label>CTA Featured Listing</Label>
                <Select value={ctaListingNorthId} onValueChange={setCtaListingNorthId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Most expensive active listing (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Most expensive active listing (default)</SelectItem>
                    {teamNorths.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.address}, {n.city}, {n.state}
                        {n.price ? ` — $${n.price.toLocaleString()}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose which listing photo appears in the CTA section
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Brand Colors */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Custom Colors
            </CardTitle>
            <CardDescription>
              Override your team's default brand colors for the Spotlight page. Leave blank to use your team defaults.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryColor">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="primaryColor"
                    value={customPrimaryColor}
                    onChange={(e) => setCustomPrimaryColor(e.target.value)}
                    placeholder="#3B82F6"
                    className="font-mono"
                  />
                  {customPrimaryColor && (
                    <div
                      className="h-9 w-9 rounded-md border border-border shrink-0"
                      style={{ backgroundColor: customPrimaryColor }}
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryColor">Secondary Color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="secondaryColor"
                    value={customSecondaryColor}
                    onChange={(e) => setCustomSecondaryColor(e.target.value)}
                    placeholder="#9333EA"
                    className="font-mono"
                  />
                  {customSecondaryColor && (
                    <div
                      className="h-9 w-9 rounded-md border border-border shrink-0"
                      style={{ backgroundColor: customSecondaryColor }}
                    />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vanity URL */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Vanity URL
            </CardTitle>
            <CardDescription>
              Create a short, memorable link for your Spotlight page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="slug">Custom Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {window.location.origin}/s/
                </span>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="your-team-name"
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Only lowercase letters, numbers, and hyphens. Leave blank to use the default URL.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Social Links
            </CardTitle>
            <CardDescription>
              Add your social media profiles to the footer of your Spotlight page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="socialFacebook">Facebook</Label>
                <Input
                  id="socialFacebook"
                  value={socialFacebook}
                  onChange={(e) => setSocialFacebook(e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="socialInstagram">Instagram</Label>
                <Input
                  id="socialInstagram"
                  value={socialInstagram}
                  onChange={(e) => setSocialInstagram(e.target.value)}
                  placeholder="https://instagram.com/yourhandle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="socialLinkedin">LinkedIn</Label>
                <Input
                  id="socialLinkedin"
                  value={socialLinkedin}
                  onChange={(e) => setSocialLinkedin(e.target.value)}
                  placeholder="https://linkedin.com/company/yourteam"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="socialTwitter">X (Twitter)</Label>
                <Input
                  id="socialTwitter"
                  value={socialTwitter}
                  onChange={(e) => setSocialTwitter(e.target.value)}
                  placeholder="https://x.com/yourhandle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="socialYoutube">YouTube</Label>
                <Input
                  id="socialYoutube"
                  value={socialYoutube}
                  onChange={(e) => setSocialYoutube(e.target.value)}
                  placeholder="https://youtube.com/@yourchannel"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="socialTiktok">TikTok</Label>
                <Input
                  id="socialTiktok"
                  value={socialTiktok}
                  onChange={(e) => setSocialTiktok(e.target.value)}
                  placeholder="https://tiktok.com/@yourhandle"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-12">
          <Button onClick={handleSave} disabled={isSaving} size="lg">
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Settings
          </Button>
        </div>
      </div>

      <SpotlightIntroModal isOpen={showIntroModal} onClose={handleCloseIntro} />
    </div>
  );
}
